import {
  faBars,
  faChevronDown,
  faChevronUp,
  faMinus,
  faPlus,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useState } from "react";
import { Layer, Source, SourceProps } from "react-map-gl/maplibre";
import { Link, useMatch } from "react-router-dom";
import { ColorBox } from "../../components/ColorBox";
import { SourceLayer, useCaMap, useCode, useLayers } from "../../state";
import { GroupedCodesDTO } from "../../state/types";
import { CustomControl } from "./CustomControl";

const IDENTBG = "LayersControlBG";
const IDENT = "LayersControl";

function LayerSubItem({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-50">
      <button
        className="w-full flex flex-row justify-between items-center px-3 py-2 text-sm font-medium text-dark-blue hover:bg-gray-50"
        onClick={() => setOpen(!open)}
      >
        <span className="mr-2">{title}</span>
        <FontAwesomeIcon
          icon={open ? faMinus : faPlus}
          className="text-xs text-gray-400 flex-shrink-0"
        />
      </button>
      {open && <div className="px-4 py-2 text-xs text-gray-600 bg-gray-50">{children}</div>}
    </div>
  );
}

const Sub = ({ children }) => (
  <div className="border-b border-gray-50">
    <span className="w-full flex flex-row justify-between items-center px-3 py-2 text-sm text-dark-blue hover:bg-gray-50">
      {children}
    </span>
  </div>
);

const Legend = ({ codes }) =>
  (codes?.length && (
    <LayerSubItem title="Legenda">
      <ul className="list-none p-0 m-0 space-y-1">
        {codes.map((c, i) => (
          <li key={i} className="flex items-center text-xs text-gray-700">
            <ColorBox color={c.color || "#234567"} />
            <span className="ml-2">{c.name}</span>
          </li>
        ))}
      </ul>
    </LayerSubItem>
  )) ||
  null;

function LayerItem({
  layer,
  codes,
  opacity,
  setOpacity,
  initialOpen = false,
}: {
  layer: SourceLayer;
  codes: GroupedCodesDTO[];
  opacity: number;
  setOpacity: (o: number) => void;
  initialOpen: boolean;
}) {
  const [open, setOpen] = useState(initialOpen);
  const toggle = useLayers((s) => s.toggle);
  return (
    <div className="bg-white rounded-lg shadow-md mb-2 overflow-hidden border border-gray-100">
      <div
        className="cursor-pointer w-full flex justify-between items-center p-3 text-left hover:bg-gray-50 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <span className="font-semibold text-dark-blue mr-2">{layer.name}</span>
        <div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggle(layer);
            }}
          >
            <FontAwesomeIcon icon={faTrashCan} className="text-gray-400 flex-shrink-0" />
          </button>
          <FontAwesomeIcon
            icon={open ? faChevronUp : faChevronDown}
            className="pl-2 text-gray-400 flex-shrink-0"
          />
        </div>
      </div>

      {open && (
        <div className="border-t border-gray-100">
          <Sub>{layer.title}</Sub>
          <Sub>
            Transparantie
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={opacity}
              onChange={(e) => setOpacity(parseFloat(e.target.value))}
              className="ml-2 w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-dark-blue"
            />
          </Sub>
          <Legend codes={codes} />
        </div>
      )}
    </div>
  );
}

const LayersLink = () => {
  const active = !!useMatch("/layers");
  return (
    <Link
      to={active ? "" : "/layers"}
      className={
        "text-xs text-gray-400 px-1 " +
        (active ? "bg-gray-300 hover:bg-transparent" : "hover:bg-gray-300")
      }
    >
      <FontAwesomeIcon icon={faBars} />
    </Link>
  );
};

function PopupControl({
  layers,
  codesPerLayer,
}: {
  layers: SourceLayer[];
  codesPerLayer: { [k: string]: GroupedCodesDTO[] };
}) {
  const [open, setOpen] = useState(false);
  const opacities = useLayers((s) => s.opacities);
  const setOpacity = useLayers((s) => s.setOpacity);

  const toggleOpen = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  return (
    <CustomControl position="top-right" className="maplibregl-ctrl" style="p ing: 0;">
      <div className="maplibregl-ctrl maplibregl-ctrl-group">
        <button
          className="maplibregl-ctrl-button maplibregl-ctrl-layers relative"
          type="button"
          title="Actieve lagen"
          aria-label="Actieve lagen"
          onClick={toggleOpen}
        >
          <span className="maplibregl-ctrl-icon" aria-hidden="true" />
          {layers.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {layers.length}
            </span>
          )}
        </button>
      </div>
      <div className="flex flex-col items-end">
        {open && (
          <div className="mt-2 w-72 max-h-[80vh] overflow-y-auto bg-gray-100/90 backdrop-blur-sm p-3 rounded-xl shadow-xl border border-white/20">
            <div className="flex justify-between mb-2">
              <h3 className="text-lg font-bold text-dark-blue px-1 inline-block">Kaartlagen</h3>
              <div>
                <LayersLink />
                <button className="text-xs text-gray-400 px-1" onClick={toggleOpen}>
                  X
                </button>
              </div>
            </div>
            {/* TODO enable layer order modfication */}
            {layers.map((layer) => (
              <LayerItem
                key={layer.name}
                layer={layer}
                codes={codesPerLayer[layer.name]}
                opacity={opacities[layer.name] ?? 0.8}
                setOpacity={(o) => setOpacity(layer.name, o)}
                initialOpen={layers.length === 1}
              />
            ))}
          </div>
        )}
      </div>
    </CustomControl>
  );
}

interface LayerProps {
  source: string;
  opacity: number;
}
const BlackLayer = ({ source, opacity }: LayerProps) => (
  <Layer
    id={source + "_black"}
    source={source}
    source-layer="default"
    beforeId="parcel"
    type="line"
    paint={{
      "line-color": "black",
      "line-width": 2,
      "line-opacity": opacity,
    }}
  />
);

const ColoredLayer = ({
  source,
  opacity,
  codes,
}: LayerProps & {
  codes: GroupedCodesDTO[];
}) => {
  if (!codes.length) return null;
  const matchListColor = [
    ...Object.values(codes).reduce(
      (acc, e) => [...acc, e.codes, e.color || "#234567"],
      ["match", ["get", "code_id"]]
    ),
    "rgba(0,0,0, 0.3)",
  ];

  return (
    <Layer
      id={source + "_colored"}
      source={source}
      source-layer="default"
      beforeId="parcel"
      type="fill"
      paint={{
        "fill-outline-color": "rgb(7, 119, 53)",
        // @ts-ignore
        "fill-color": matchListColor,
        "fill-opacity": opacity,
      }}
    />
  );
};

const LabelLayer = ({
  source,
  opacity,
  type,
}: LayerProps & {
  type: string;
}) => (
  <Layer
    id={source + "_label"}
    source={source}
    source-layer="default"
    beforeId="parcel"
    type="symbol"
    layout={{
      "text-rotation-alignment": "map",
      "text-anchor": "top",
      "text-field": "{name}",
      "text-font": ["Noto Sans Regular"],
      "text-size": 14,
      "symbol-placement": type === "Road" ? "line" : "point",
    }}
    minzoom={7}
    maxzoom={type === "Road" ? 24 : 15}
    paint={{
      "text-color": "#000",
      "text-halo-blur": 1,
      "text-halo-color": "rgba(255,255,255,0.75)",
      "text-halo-width": 1,
      "text-opacity": opacity,
    }}
  />
);

const OverlayLayer = ({
  layer,
  codesPerLayer,
}: {
  layer: SourceLayer;
  codesPerLayer: { [k: string]: GroupedCodesDTO[] };
}) => {
  const opacities = useLayers((s) => s.opacities);
  const id = layer.name;
  const opacity = opacities[id] ?? 0.5;
  const codes = codesPerLayer[id];
  const props: SourceProps = {
    id: IDENT + id,
    type: layer.type,
    url: layer.url,
    promoteId: "id",
  };
  const lProps = { source: props.id, opacity: opacity };
  return (
    <Source {...props}>
      {layer.labels && <LabelLayer type={layer.type} {...lProps} />}
      {layer.type === "raster" ? (
        <Layer
          id={props.id}
          type="raster"
          beforeId="parcel"
          {...lProps}
          paint={{ "raster-opacity": opacity }}
        />
      ) : codes && codes.length ? (
        <ColoredLayer codes={codesPerLayer[id]} {...lProps} />
      ) : (
        <BlackLayer {...lProps} />
      )}
    </Source>
  );
};

export const LayersControl = () => {
  const map = useCaMap();
  const { backgroundId, layers, selected } = useLayers();
  const { codesPerLayer, fetchMissingCodes } = useCode();
  if (!map) return null;

  let background;
  if (backgroundId !== "default") {
    const bg = layers.find((l) => l.name === backgroundId);
    if (bg) {
      background = {
        type: "raster",
        tiles: [bg.url],
        tileSize: 256,
        ...bg.attributes,
      };
    }
  }

  if (selected?.length) fetchMissingCodes(selected);

  return (
    <>
      {background && (
        <Source id={IDENTBG} {...background}>
          <Layer id={IDENTBG} type="raster" source={IDENTBG} beforeId="parcel" />
        </Source>
      )}
      {selected.map((layer) => (
        <OverlayLayer key={layer.name} layer={layer} codesPerLayer={codesPerLayer} />
      ))}
      {selected.length && <PopupControl layers={selected} codesPerLayer={codesPerLayer} />}
    </>
  );
};
